import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, DISCIPLINES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, FileText, Compass, Trash2, PenLine, BellRing, CalendarClock, BookOpen,
  ScanSearch, ShieldCheck, GraduationCap, Wrench, Sparkles
} from "lucide-react";

function daysUntil(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000*60*60*24));
}

const QUICK_ACTIONS = [
  { to: "/app/funding", icon: Compass, label: "Discover funding", tone: "Search live opportunities" },
  { to: "/app/analyzer", icon: ScanSearch, label: "Analyse a notification", tone: "Upload or paste a URL" },
  { to: "/app/plagiarism", icon: ShieldCheck, label: "Originality check", tone: "Score your draft" },
  { to: "/app/papers", icon: BookOpen, label: "Write a paper", tone: "IEEE / Scopus / Nature" },
  { to: "/app/learn", icon: GraduationCap, label: "Learn grantsmanship", tone: "Curated resources" },
  { to: "/app/tools", icon: Wrench, label: "Open tools", tone: "Notepad · calc · calendar" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [papers, setPapers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", discipline: user?.discipline || "Sciences", agency: "" });
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, pp] = await Promise.all([api.get("/proposals"), api.get("/funding/alerts"), api.get("/papers")]);
      setProposals(p.data); setAlerts(a.data); setPapers(pp.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const urgentAlerts = alerts
    .map(a => ({ ...a, _d: daysUntil(a.deadline) }))
    .filter(a => a._d !== null && a._d >= 0 && a._d <= 60)
    .sort((x,y) => x._d - y._d);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Please give it a working title");
    try {
      const { data } = await api.post("/proposals", form);
      toast.success("New proposal created");
      setOpen(false);
      nav(`/app/proposals/${data.id}`);
    } catch { toast.error("Failed to create"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this proposal?")) return;
    await api.delete(`/proposals/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="overline">Your studio</div>
          <h1 className="font-display text-5xl mt-2">Good day, {user?.full_name?.split(" ")[0]}.</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">Pick up a draft, discover a call, or start something new. Everything you create stays yours.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/funding"><Button variant="outline" className="rounded-full" data-testid="btn-open-funding"><Compass className="w-4 h-4 mr-2"/>Funding feed</Button></Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-new-proposal"><Plus className="w-4 h-4 mr-2"/>New Proposal</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Start a new proposal</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Working title</Label>
                  <Input placeholder="e.g., Explainable AI for Radiology" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="new-title"/>
                </div>
                <div className="space-y-2">
                  <Label>Discipline</Label>
                  <Select value={form.discipline} onValueChange={(v)=>setForm({...form, discipline:v})}>
                    <SelectTrigger data-testid="new-discipline"><SelectValue/></SelectTrigger>
                    <SelectContent>{DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target agency (optional)</Label>
                  <Input placeholder="e.g., NSF, ERC…" value={form.agency} onChange={(e)=>setForm({...form, agency:e.target.value})} data-testid="new-agency"/>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="new-create-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="dashboard-stats">
        <Stat label="Proposals" value={proposals.length} to="/app"/>
        <Stat label="Papers" value={papers.length} to="/app/papers"/>
        <Stat label="Alerts" value={alerts.length} to="/app/alerts"/>
        <Stat label="Due < 30d" value={urgentAlerts.filter(a=>a._d<=30).length} to="/app/alerts" alert={urgentAlerts.filter(a=>a._d<=30).length>0}/>
      </div>

      {/* Quick actions */}
      <div className="overline mb-3">Quick actions</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8" data-testid="quick-actions">
        {QUICK_ACTIONS.map(a => (
          <Link key={a.to} to={a.to} className="card-crisp rounded-lg p-4 hover:border-primary/60 transition-colors group">
            <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center mb-3 group-hover:bg-primary group-hover:text-primary-foreground transition-colors"><a.icon className="w-5 h-5"/></div>
            <div className="font-display text-lg leading-tight">{a.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{a.tone}</div>
          </Link>
        ))}
      </div>

      {urgentAlerts.length > 0 && (
        <div className="mb-8 rounded-lg border border-red-300 bg-red-50 p-5" data-testid="urgent-alerts">
          <div className="flex items-center gap-2 mb-3">
            <BellRing className="w-5 h-5 text-red-700"/>
            <div className="font-display text-xl text-red-900">Upcoming deadlines</div>
          </div>
          <div className="space-y-2">
            {urgentAlerts.slice(0,5).map(a => (
              <Link to="/app/alerts" key={a.id} className="flex items-center justify-between gap-4 text-sm hover:bg-red-100/60 rounded px-2 py-1.5" data-testid={`urgent-alert-${a.id}`}>
                <div>
                  <span className="font-medium text-red-900">{a.name}</span>
                  <span className="text-red-700/70 ml-2">· {a.agency}</span>
                </div>
                <span className={`inline-flex items-center gap-1 font-medium ${a._d<=7 ? "text-red-800" : "text-amber-800"}`}><CalendarClock className="w-3.5 h-3.5"/>{a._d}d · {a.deadline}</span>
              </Link>
            ))}
            {urgentAlerts.length > 5 && <Link to="/app/alerts" className="text-sm text-red-800 hover:underline">See all {urgentAlerts.length} →</Link>}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="overline">My proposals</div>
            <Button size="sm" variant="ghost" onClick={()=>setOpen(true)} data-testid="prop-inline-new"><Plus className="w-4 h-4"/></Button>
          </div>
          {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
           proposals.length === 0 ? (
            <div className="card-crisp p-6 rounded-lg text-center" data-testid="empty-state">
              <div className="font-display text-2xl">Your first proposal awaits</div>
              <Button className="mt-3 rounded-full" onClick={()=>setOpen(true)}><Plus className="w-4 h-4 mr-2"/>Begin</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {proposals.slice(0,5).map(p => (
                <div key={p.id} className="card-crisp rounded-lg p-3 flex items-center gap-3 group" data-testid={`prop-card-${p.id}`}>
                  <div className="w-9 h-9 rounded-md bg-primary/10 text-primary grid place-items-center"><FileText className="w-4 h-4"/></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.title}</div>
                    <div className="text-[11px] text-muted-foreground">{p.discipline} · {p.agency || "Agency TBD"} · updated {new Date(p.updated_at).toLocaleDateString()}</div>
                  </div>
                  <Link to={`/app/proposals/${p.id}`}><Button variant="outline" size="sm" data-testid={`prop-open-${p.id}`}><PenLine className="w-3.5 h-3.5"/></Button></Link>
                  <button onClick={()=>del(p.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" data-testid={`prop-del-${p.id}`}><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
              {proposals.length > 5 && <div className="text-xs text-muted-foreground text-center pt-1">Showing 5 of {proposals.length}</div>}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="overline">My papers</div>
            <Link to="/app/papers"><Button size="sm" variant="ghost"><Plus className="w-4 h-4"/></Button></Link>
          </div>
          {papers.length === 0 ? (
            <div className="card-crisp p-6 rounded-lg text-center">
              <div className="font-display text-2xl">No manuscripts yet</div>
              <Link to="/app/papers"><Button className="mt-3 rounded-full"><Sparkles className="w-4 h-4 mr-2"/>Start writing</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {papers.slice(0,5).map(p => (
                <Link to={`/app/papers/${p.id}`} key={p.id} className="card-crisp rounded-lg p-3 flex items-center gap-3 hover:border-primary/60">
                  <div className="w-9 h-9 rounded-md bg-primary/10 text-primary grid place-items-center"><BookOpen className="w-4 h-4"/></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.title}</div>
                    <div className="text-[11px] text-muted-foreground">{p.journal_template}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, to, alert }) {
  return (
    <Link to={to} className={`rounded-lg border p-4 hover:border-primary/60 transition-colors ${alert ? "border-red-300 bg-red-50" : "border-border bg-card"}`}>
      <div className="overline">{label}</div>
      <div className={`font-display text-4xl mt-1 ${alert ? "text-red-800" : "text-foreground"}`} style={{fontFeatureSettings: '"lnum" 1, "tnum" 1'}}>{value}</div>
    </Link>
  );
}
